/**
 * The collapsable rows are wordking, you ust need to
 * - uncomment the last item of `headerColumns`.
 * - uncomment the `handleRowCollapse` function.
 * - uncomment the `formatCollapse` function.
 */

import "./style.scss";
import "../ImportData/style.scss";
import "../ImportData/style.scss";

import { Tab, Tooltip } from "@mui/material";
import CorrectionPanel from "components/ImportData/CorrectionPanel";
import { showImportProgressToast } from "components/ImportData/ProgressToast";
import { formatImportAnomalies } from "components/ImportData/utils";
import { Pending } from "components/Management/ModalContent";
import ModalBox from "components/Modal";
import ControlFilter from "components/shared/ControlFilter";
import CopyToClipboard from "components/shared/CopyToClipboard";
import type { ICorrectionColumnData } from "components/shared/CorrectionTable";
import CorrectionTable from "components/shared/CorrectionTable";
import DeleteModal from "components/shared/DeleteModal";
import EmptyState from "components/shared/EmptyState";
import type { LastElementTableAPIData, LastElementTableColumns } from "components/shared/LastElementTable/type";
import ModalBase from "components/shared/ModalBase";
import SmallSearchBar from "components/shared/SmallSearchBar";
import type { IStatusRule } from "components/shared/StatusChip";
import StatusChip from "components/shared/StatusChip";
import { StyledTabs } from "components/shared/StyledTabs";
import { WhiteDropdownMenu } from "components/shared/WhiteDropdownMenu";
import { useAuth } from "contexts/auth/AuthContext";
import { PortalType } from "contexts/portal.context";
import format from "date-fns/format";
import { SmallBody, TinyBody } from "helpers/TypographyComponents";
import { isObjectEmpty } from "helpers/utils";
import useAPIPagination from "hooks/useAPIPagination";
import { useCompanySettings } from "hooks/useCompanySettings";
import usePageTitle from "hooks/usePageTitle";
import React, { useContext, useEffect, useMemo, useState } from "react";
import type { TFunction } from "react-i18next";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type { TImportError } from "store/dataImportSlice";
import { abortProcessingImport, downloadAnomaliesFile, revalidateImportSalesTable } from "store/dataImportSlice";
import type { IFile, ImportSourceType } from "store/filesHistorySlice";
import {
    fetchAnomaliesData,
    fetchAnomaliesReportAsFile,
    fetchExportedFile,
    fetchImportedFile,
    resetImportFiles,
    setSingleExportedFileIdleStatus,
    setSingleImportedFileIdleStatus,
} from "store/filesHistorySlice";
import { fetchInvoicePercentages } from "store/invoicePercentages";
import type { ImportJobStep } from "store/jobsStore";
import { fetchJob, ProcessingJobStatus, rollbackFile, setJobStatus } from "store/jobsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { EPortalType } from "store/types";
import type { TExportTargets, TImportTargets } from "types";
import { ExportTargetsEnum } from "types";
import {
    BookExportTargetsArray,
    BookImportTargetsArray,
    PressExportTargetsArray,
    PressImportTargetsArray,
    TransfereeExportTargetsArray,
} from "types";

import Import from "../../assets/img/svg/import";
import CSV from "../../assets/img/svg/importExport/csv";
import { clearInvoice, fetchBexioContacts } from "../../store/bexioStore";
import LastElementTable from "../shared/LastElementTable";
import StyledButton from "../shared/StyledButton";
import ProgressChip from "./ProgressChip";
import { SendInvoiceModal } from "./SendInvoiceModal";
import { formatErrorColumns, formatErrorsEntries } from "./utils";

export const jobStatusRules = (t: TFunction<"translation", undefined>): Record<number, IStatusRule> => ({
    [ProcessingJobStatus.FAILED]: {
        text: t("History.JobsStatus.Failed"),
        color: "red",
    },
    [ProcessingJobStatus.PROCESSING]: {
        text: t("History.JobsStatus.Processing"),
        color: "yellow",
    },
    [ProcessingJobStatus.QUEUED]: {
        text: t("History.JobsStatus.Queued"),
        color: "grey",
    },
    [ProcessingJobStatus.DONE]: {
        text: t("History.JobsStatus.Done"),
        color: "green",
    },
    [ProcessingJobStatus.REVERTED]: {
        text: t("History.JobsStatus.Reverted"),
        color: "blue",
    },
    [ProcessingJobStatus.IDLE]: {
        text: t("History.JobsStatus.ActionRequired"),
        color: "orange",
    },
});

const TERMINAL_JOB_STATUSES = new Set([
    ProcessingJobStatus.DONE,
    ProcessingJobStatus.FAILED,
    ProcessingJobStatus.REVERTED,
]);

const resolveImportJobStatus = (listStatus?: ProcessingJobStatus, storeStatus?: ProcessingJobStatus) => {
    if (storeStatus !== undefined && TERMINAL_JOB_STATUSES.has(storeStatus)) {
        return storeStatus;
    }
    if (listStatus !== undefined && TERMINAL_JOB_STATUSES.has(listStatus)) {
        return listStatus;
    }
    return storeStatus ?? listStatus;
};

enum EHistoryTabs {
    IMPORT = 0,
    EXPORT = 1,
}

type TSourceFilterOptions = {
    label: string;
    value: ImportSourceType | "all";
};

function History() {
    const dispatch = useAppDispatch();
    const [t] = useTranslation();
    const {
        importedFiles,
        importedFilesCount,
        exportedFiles,
        exportedFilesCount,
        exportedFilesStatus,
        importedFilesStatus,
        importedFileStatus,
        exportedFileStatus,
    } = useAppSelector((state) => state.filesHistory);

    const { getCompanySettings } = useCompanySettings();
    const { isAdminCrealo } = useAuth();
    const ftp_username = getCompanySettings("ftp_username");
    const uses_transfer_term = getCompanySettings("uses_transfer_term");
    const portalType = useContext(PortalType);

    const { hasBexioIntegration } = useAppSelector((state) => state.integrations);

    const [tabsValue, setTabsValue] = useState(EHistoryTabs.IMPORT);
    const [data, setData] = useState<IFile[]>([]);
    /**
     * @note We use sourceType filter mainly to separate SFTP imports (Auzou)
     */
    const showSourceTypeFilter = !!ftp_username && tabsValue === EHistoryTabs.IMPORT;

    const [openModal, setOpenModal] = useState(false);
    const [modalType, setModalType] = useState("");
    const [currentJobId, setCurrentJobId] = useState<number | null>(null);

    const [selectedFile, setSelectedFile] = useState<IFile | null>(null);
    const [anomalies, setAnomalies] = useState<TImportError[]>([]);
    const [errorColumns, setErrorColumns] = useState<ICorrectionColumnData[]>([]);

    const { bexioContacts, createdInvoice, failureReasonOnCreateInvoice } = useAppSelector((state) => state.bexio);
    const { invoicePercentages, invoicePercentagesStatus } = useAppSelector((state) => state.invoicePercentages);
    const { jobs } = useAppSelector((state) => state.jobs);
    const { pendingImports } = useAppSelector((state) => state.dataImport);

    const [modal, setModal] = useState<{ type: string; data?: any } | null>(null);

    usePageTitle(t("History.Title"));

    const {
        requestStatus,
        count,
        page,
        limitPagination,
        handleChangeLimit,
        handleChangePage,
        handleSort,
        query,
        handleQuery,
        handleRequest,
        target,
        setTarget,
        sourceType,
        setSourceType,
    } = useAPIPagination({
        type: tabsValue === EHistoryTabs.IMPORT ? "importFiles" : "exportFiles",
        sortBy: "id",
        sortOrder: "desc",
    });

    const APIData: LastElementTableAPIData = {
        count,
        page,
        limitPagination,
        handleChangeLimit,
        handleChangePage,
        handleSort,
    };

    const dataLoading = ["pending", "idle"].includes(requestStatus);

    const files = useMemo(() => {
        let _files: IFile[] = [];
        if (tabsValue === EHistoryTabs.IMPORT && importedFiles) {
            _files = structuredClone(importedFiles);
        } else if (tabsValue === EHistoryTabs.EXPORT && exportedFiles) {
            _files = structuredClone(exportedFiles);
        }
        return _files;
    }, [importedFiles, tabsValue, exportedFiles, pendingImports]);

    useEffect(() => {
        setData(files);
    }, [files, tabsValue]);

    const handleChangeTabsValue = (_: React.SyntheticEvent, newValue: number) => {
        setTabsValue(newValue);

        switch (newValue) {
            case EHistoryTabs.IMPORT:
                handleRequest({ defaultType: "importFiles" });
                break;
            case EHistoryTabs.EXPORT:
                handleRequest({ defaultType: "exportFiles" });
                break;
        }
    };

    const handleDownloadFile = (file: IFile) => {
        if (tabsValue === EHistoryTabs.IMPORT) {
            dispatch(fetchImportedFile(file.id));
            return;
        }

        const isLegacyTaxDeclaration =
            file.target === ExportTargetsEnum.taxDeclaration && file.metadata?.streamed !== true;

        dispatch(
            fetchExportedFile({
                id: file.id,
                filename: file.filename,
                isBlob: isLegacyTaxDeclaration,
            }),
        );
    };

    const openCorrectionPanel = (currentRow: IFile) => {
        setModal({
            type: "correction-sales",
            data: {
                fileId: currentRow.id,
                jobId: currentRow.processingJobId,
                target: currentRow.target,
                step: currentRow.jobMetadata?.step || "",
            },
        });
    };

    const handleSeeInvoice = (id: number) => {
        // redirect to invoice page
        window.open(`https://office.bexio.com/index.php/kb_invoice/show/id/${id}`, "_blank");
    };

    const handleGenerateInvoice = (file: IFile) => {
        setSelectedFile(file);
        setOpenModal(true);
    };

    // Works for all files, but we're only doing it for last sales import:
    const handleRollbackFile = async () => {
        if (currentJobId) {
            const response = await dispatch(rollbackFile({ jobId: currentJobId }));
            if (response.meta.requestStatus === "fulfilled") {
                dispatch(resetImportFiles(currentJobId));
                setModalType("");
            }
        }
    };

    useEffect(() => {
        clearInvoice();
    }, []);

    // Likewise here: why was it needed?
    useEffect(() => {
        if (["failed", "succeeded"].includes(exportedFileStatus)) {
            dispatch(setSingleExportedFileIdleStatus());
        }
        if (["failed", "succeeded"].includes(importedFileStatus)) {
            dispatch(setSingleImportedFileIdleStatus());
        }
    }, [importedFileStatus, exportedFileStatus, dispatch]);

    useEffect(() => {
        if (!bexioContacts.length && hasBexioIntegration) {
            dispatch(fetchBexioContacts());
        }
    }, [hasBexioIntegration]);

    useEffect(() => {
        if (hasBexioIntegration) {
            if (invoicePercentagesStatus === "idle") {
                dispatch(fetchInvoicePercentages());
            }
        }
    }, [hasBexioIntegration, invoicePercentagesStatus]);

    type TFormat = (value: any, id?: number, currentRow?: IFile) => JSX.Element;

    const handleRollbackModal = (jobId: number | null, type: TImportTargets) => {
        if (!["sales", "beneficiar", "title", "terms"].includes(type) || !jobId) return;
        setModalType("revert");
        setCurrentJobId(jobId);

        const currentFile = data?.find((file) => file.processingJobId === jobId);
        if (currentFile) {
            setData(
                (prev) =>
                    prev?.map((item) => {
                        if (item.id === currentFile.id) {
                            return currentFile;
                        } else {
                            return item;
                        }
                    }),
            );
        }
    };

    /**
     * Download anomalies file (only possible for asynchronous imports.)
     */
    const handleAnomaliesDownload = async () => {
        if (!anomalies || !selectedFile) return;
        const formattedData = formatImportAnomalies({ errors: anomalies });
        if (!isObjectEmpty(formattedData)) {
            const fileName = selectedFile.filename.split(".")[0];
            const anomaliesToast = toast.loading(`${t("Export.Pending")}`);
            const response = await dispatch(downloadAnomaliesFile({ excelData: formattedData, fileName }));
            if (response.meta.requestStatus === "fulfilled") {
                toast.update(anomaliesToast, {
                    render: `${t("Export.Successful")}`,
                    type: "success",
                    isLoading: false,
                    autoClose: 1000,
                });
            } else {
                toast.update(anomaliesToast, {
                    render: `${t("Export.Error")}`,
                    type: "error",
                    isLoading: false,
                    autoClose: 1000,
                });
            }
        }
    };

    const getSalesImportChannelLabel = (file?: IFile) => {
        if (file?.target !== "sales") {
            return null;
        }

        const metadata = file.metadata;
        if (!metadata || !("channelId" in metadata)) {
            return null;
        }

        if (metadata.channelId == null) {
            return t("History.ImportAllChannels");
        }

        return t("History.ImportChannel", { id: metadata.channelId });
    };

    const formatDocumentType: TFormat = (value, _, currentRow) => {
        const channelLabel = getSalesImportChannelLabel(currentRow);

        return (
            <div>
                <div>{t(`History.Type.${value}`)}</div>
                {channelLabel && <TinyBody color="grey">{channelLabel}</TinyBody>}
            </div>
        );
    };

    const formatFilename: TFormat = (value, id, currentRow) => {
        return (
            <div className="history-wrapper-table-row-file-name" title={value}>
                <CSV style={{ height: "18px", width: "18px" }} />
                <div className="file-title-txt-wrapper">
                    <Tooltip title={value}>
                        <div className="file-title-txt">{value || "no currentRow.filename"}</div>
                    </Tooltip>
                    <TinyBody color="grey">
                        {currentRow?.numberOfLines} {t("History.Lines")}{" "}
                    </TinyBody>
                </div>
            </div>
        );
    };

    const formatDate: TFormat = (value) => {
        return <span>{format(new Date(value), "dd/MM/yyyy")}</span>;
    };

    /**
     * Opens anomalies modal.
     */
    const renderAnomaliesModal = async (currentImport: IFile) => {
        if (!currentImport) return;
        const response = await dispatch(fetchAnomaliesData({ importId: currentImport.id }));
        if (response.meta.requestStatus === "fulfilled") {
            const _anomalies = response.payload.data;
            setAnomalies(_anomalies);
            if (!_anomalies.length) {
                toast.error(`${t("ServerError.NoAnomaliesFound")}`);
                return;
            }
            setErrorColumns(formatErrorColumns(_anomalies[0].entry));
            setModalType("anomalies");
        }
    };

    const downloadAnomaliesReport = async (currentImport: IFile) => {
        if (!currentImport) return;
        await dispatch(fetchAnomaliesReportAsFile({ importId: currentImport.id }));
    };

    const handleAbortImport = async (currentImport: IFile) => {
        if (!currentImport?.processingJobId) return;
        const response = await dispatch(abortProcessingImport({ jobId: currentImport.processingJobId }));
        if (response.meta.requestStatus === "fulfilled") {
            dispatch(resetImportFiles(currentImport.processingJobId));
            dispatch(setJobStatus({ id: currentImport.processingJobId, status: ProcessingJobStatus.REVERTED }));
            dispatch(fetchJob({ jobId: currentImport.processingJobId }));
        }
    };

    /**
     * Formats action button.
     * Depending on type of file and its state the button and its options won't be the same.
     */
    const formatAction: TFormat = (currentRow: IFile) => {
        const acceptedTargetsForRevert = ["sales", "beneficiar", "title", "terms"];

        if (!currentRow) {
            return <></>;
        }

        const isSales = currentRow.target === "sales";
        const isBeneficiaries = currentRow.target === "beneficiar";

        const hasBeenInvoiced =
            isSales && currentRow.metadata && typeof currentRow.metadata === "object" && currentRow.metadata?.invoiceId;
        const isInvoiceable = typeof currentRow.metadata === "object" && !!currentRow.metadata?.invoiceable;
        const showBexioInvoiceAction = isSales && hasBexioIntegration && (!!hasBeenInvoiced || isInvoiceable);

        const filesOfType = data?.filter((file) => file.target === currentRow.target) ?? [];

        const isLastFile = filesOfType.length ? filesOfType[0].id === currentRow.id : false;
        const canBeReverted = isLastFile || (isAdminCrealo && isSales) || (isAdminCrealo && isBeneficiaries);
        const resolvedJobStatus = resolveImportJobStatus(
            currentRow.jobStatus,
            jobs.find((item) => item.id === currentRow.processingJobId)?.status,
        );
        const wasFileReverted = resolvedJobStatus === ProcessingJobStatus.REVERTED;

        const correctionRequired =
            tabsValue === EHistoryTabs.IMPORT &&
            resolvedJobStatus === ProcessingJobStatus.IDLE &&
            currentRow.jobMetadata?.step === "corrections-required";

        const isFailedImport = tabsValue === EHistoryTabs.IMPORT && currentRow.metadata.iwfId;

        const currentRowJobStep = currentRow.jobMetadata?.step;

        const buttonOptions = [
            {
                id: 1,
                name: t("History.DownloadFile"),
                action: () => currentRow && handleDownloadFile(currentRow),
                hide: !isSales || !hasBexioIntegration,
            },
            {
                id: 2,
                name: t("History.SeeAnomalies"),
                action: () =>
                    currentRow && correctionRequired
                        ? downloadAnomaliesReport(currentRow)
                        : renderAnomaliesModal(currentRow),
                hide: !correctionRequired && !isFailedImport,
            },
            {
                id: 3,
                name: t("History.Rollback.CancelFile"),
                action: () =>
                    currentRow && correctionRequired
                        ? handleAbortImport(currentRow)
                        : handleRollbackModal(currentRow.processingJobId, currentRow.target as TImportTargets),
                disabled: !correctionRequired && (!canBeReverted || wasFileReverted),
                // Failed import can never be rollbacked. It doesn't make sense.
                hide:
                    !correctionRequired && (!!isFailedImport || !acceptedTargetsForRevert.includes(currentRow.target)),
                tooltip: wasFileReverted
                    ? t("History.Rollback.AlreadyCancelled")
                    : canBeReverted
                      ? ""
                      : t("History.Rollback.ImpossibleRevert"),
            },
        ];

        const importActionButtonStyle: React.CSSProperties = {
            width: "154px",
            minWidth: "154px",
        };
        const invoiceActionButtonStyle: React.CSSProperties = {
            minWidth: "154px",
        };
        const billedInvoiceButtonStyle: React.CSSProperties = {
            ...invoiceActionButtonStyle,
            color: "#275ab0",
        };

        switch (tabsValue) {
            case EHistoryTabs.IMPORT:
                if (resolvedJobStatus === ProcessingJobStatus.IDLE && currentRowJobStep === "corrections-required") {
                    return (
                        <div style={{ display: "flex", justifyContent: "center" }}>
                            <WhiteDropdownMenu
                                options={buttonOptions}
                                buttonName={t("History.Correct")}
                                style={importActionButtonStyle}
                                mainAction={() => currentRow && openCorrectionPanel(currentRow)}
                            />
                        </div>
                    );
                }

                if (
                    resolvedJobStatus === ProcessingJobStatus.PROCESSING &&
                    (currentRowJobStep === "sales-importing-started" ||
                        currentRowJobStep === "sales-importing-ready" ||
                        currentRowJobStep === "raw-file-parsing-started" ||
                        currentRowJobStep === "raw-file-uploading-started")
                ) {
                    return (
                        <div style={{ display: "flex", justifyContent: "center" }}>
                            <WhiteDropdownMenu
                                options={buttonOptions}
                                buttonName={t("History.Download")}
                                style={importActionButtonStyle}
                                btnIcon={<Import height={"13px"} width={"13px"} />}
                                onMenuClick={() => setSelectedFile(currentRow)}
                                mainAction={() => currentRow && handleDownloadFile(currentRow)}
                            />
                        </div>
                    );
                }

                // Bexio sales: show generate/see only when already invoiced or import has an invoiceable amount.
                // Otherwise fall back to the basic download action (nothing to invoice).
                return showBexioInvoiceAction ? (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <WhiteDropdownMenu
                            options={buttonOptions}
                            buttonName={hasBeenInvoiced ? t("Invoice.See") : t("Invoice.GenerateInvoice")}
                            style={hasBeenInvoiced ? billedInvoiceButtonStyle : invoiceActionButtonStyle}
                            onMenuClick={() => setSelectedFile(currentRow)}
                            mainAction={() =>
                                hasBeenInvoiced
                                    ? handleSeeInvoice(currentRow.metadata.invoiceId!)
                                    : handleGenerateInvoice(currentRow)
                            }
                            disableMainAction={resolvedJobStatus !== ProcessingJobStatus.DONE}
                        />
                    </div>
                ) : (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <WhiteDropdownMenu
                            options={buttonOptions}
                            buttonName={t("History.Download")}
                            style={importActionButtonStyle}
                            btnIcon={<Import height={"13px"} width={"13px"} />}
                            onMenuClick={() => setSelectedFile(currentRow)}
                            mainAction={() => currentRow && handleDownloadFile(currentRow)}
                        />
                    </div>
                );
            case EHistoryTabs.EXPORT:
                return (
                    <div className={`action-btn`}>
                        <StyledButton
                            className="download"
                            buttonType="outlined"
                            tooltipText={t("History.Download")}
                            style={{ minWidth: "auto", padding: "6px 10px" } as React.CSSProperties}
                            onClick={() => {
                                currentRow && handleDownloadFile(currentRow);
                                setSelectedFile(currentRow);
                            }}
                        >
                            <Import width="12px" height="12px" />
                        </StyledButton>
                    </div>
                );
        }
    };

    const formatStatusColumn = (importData: IFile) => {
        const jobId = importData.processingJobId;
        let status;
        if (jobId) {
            const job = jobs.find((item) => item.id === jobId);
            status = resolveImportJobStatus(importData.jobStatus, job?.status);
        } else {
            status = ProcessingJobStatus.FAILED;
        }
        return (
            <div className="flex justify-center items-center min-w-[80px]!">
                <StatusChip
                    hideDot
                    status={status}
                    statusRules={jobStatusRules(t)}
                    extraElement={
                        jobId && status === ProcessingJobStatus.PROCESSING ? <ProgressChip jobId={jobId} /> : undefined
                    }
                    style={{ minWidth: "80px" }}
                />
            </div>
        );
    };

    const headerColumns: LastElementTableColumns[] = [
        {
            id: "id",
            label: "ID",
            align: "center",
            sort: true,
            hidden: !isAdminCrealo,
            format: (value: number) => (
                <div className="flex justify-center items-center w-full min-w-0">
                    <SmallBody color="grey" tag="span" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <CopyToClipboard
                            icon={false}
                            color="inherit"
                            style={{ fontSize: "inherit", fontWeight: "inherit" }}
                        >
                            {String(value)}
                        </CopyToClipboard>
                    </SmallBody>
                </div>
            ),
        },
        {
            id: "filename",
            align: "left",
            format: formatFilename,
            label: t("History.DocumentName"),
            sort: true,
        },
        {
            id: "target",
            label: t("History.DocumentType"),
            align: "center",
            sort: true,
            format: formatDocumentType,
        },
        {
            id: "date",
            label: t("History.Date"),
            align: "center",
            sort: true,
            format: formatDate,
        },
        {
            id: "user",
            label: t("History.User"),
            align: "center",
            sort: true,
        },
        {
            id: "processingJobId",
            label: t("History.Status"),
            align: "center",
            sort: true,
            format: (_: number, __, currentImport: IFile) => formatStatusColumn(currentImport),
            hidden: tabsValue === EHistoryTabs.EXPORT,
        },
        {
            align: "center",
            id: "action",
            label: t("History.Action"),
            sort: false,
            format: (_, __, currentImport: IFile) => formatAction(currentImport),
        },
    ];

    const visibleColumnsCount = headerColumns.filter((col) => col.hidden !== true).length;

    const revertModalDescription = (
        <>
            <p>{t("History.Rollback.DeleteDescription1")}</p>
            <p>{t("History.Rollback.DeleteDescription2")}</p>
            <p>{t("History.Rollback.DeleteDescription3")}</p>
        </>
    );

    const typeFilterOptions = () => {
        if (tabsValue === EHistoryTabs.IMPORT) {
            const importTargets = portalType === EPortalType.PRESS ? PressImportTargetsArray : BookImportTargetsArray;
            const fullOptions = importTargets.map((target) => ({
                label: t(`History.Type.${target}`),
                value: target,
            }));
            return [{ label: t("Filters.AllMasculine"), value: "all" }, ...fullOptions];
        } else {
            let exportTargets = portalType === EPortalType.PRESS ? PressExportTargetsArray : BookExportTargetsArray;
            if (uses_transfer_term) {
                exportTargets = [...exportTargets, ...TransfereeExportTargetsArray];
            }
            const fullOptions = exportTargets.map((target) => ({
                label: t(`History.Type.${target}`),
                value: target,
            }));
            return [{ label: t("Filters.AllMasculine"), value: "all" }, ...fullOptions];
        }
    };

    const sourceFilterOptions: TSourceFilterOptions[] = [
        {
            label: t("Filters.AllMasculine"),
            value: "all",
        },
        {
            label: t("History.SourceType.crealo"),
            value: "crealo",
        },
        {
            label: t("History.SourceType.crealo-sftp"),
            value: "crealo-sftp",
        },
        /**
         * @note Interforum SFTP will be added later.
         */
        // {
        //     label: t("History.Type.interforum-sftp"),
        //     value: "interforum-sftp",
        // },
    ];

    const handleTypeFilter = (value: TImportTargets | TExportTargets | "all") => {
        setTarget(value === "all" ? "all" : [value]);
    };

    const handleSourceTypeFilter = (value: ImportSourceType | "all") => {
        setSourceType(value);
    };

    const modalComponenent = useMemo(() => {
        if (!modal) return null;
        switch (modal.type) {
            case "correction-sales":
                if (!modal.data?.jobId) {
                    return null;
                }

                return (
                    <ModalBox
                        open
                        onClose={() => setModal(null)}
                        showCloseButton={true}
                        mainTitle={"Corrections"}
                        style={{
                            minWidth: "85vw",
                            maxWidth: "85vw",
                            overflow: "auto",
                            height: "100%",
                            position: "relative",
                        }}
                    >
                        <div className="relative h-full">
                            <div className="relative h-[60vh] overflow-auto">
                                <CorrectionPanel processingJobId={modal.data?.jobId} contentType="sales" />
                            </div>
                            <div className="flex justify-end">
                                <StyledButton
                                    className="bg-[#2D2D2D]! text-white! rounded-sm!"
                                    onClick={() => {
                                        const jobId = modal.data?.jobId;
                                        if (jobId) {
                                            setModal({
                                                type: "sales-import-status",
                                                data: { jobId: undefined },
                                            });
                                            dispatch(revalidateImportSalesTable({ jobId })).then(({ payload }) => {
                                                const revalidationRes = payload?.data as
                                                    | {
                                                          status: ProcessingJobStatus;
                                                          step: ImportJobStep;
                                                      }
                                                    | undefined;

                                                if (revalidationRes?.step === "corrections-required") {
                                                    setModal({
                                                        type: "correction-sales",
                                                        data: { jobId },
                                                    });
                                                } else {
                                                    if (revalidationRes?.status === ProcessingJobStatus.PROCESSING) {
                                                        showImportProgressToast(jobId, dispatch);
                                                    }

                                                    dispatch(fetchJob({ jobId }));
                                                    setModal(null);
                                                }
                                            });
                                        }
                                    }}
                                >
                                    Valider ces corrections
                                </StyledButton>
                            </div>
                        </div>
                    </ModalBox>
                );

            case "sales-import-status":
                return (
                    <ModalBox
                        open
                        onClose={() => setModal(null)}
                        showCloseButton={true}
                        mainTitle={"Import en cours"}
                        style={{
                            minWidth: "85vw",
                            maxWidth: "85vw",
                            overflow: "auto",
                            height: "100%",
                            position: "relative",
                        }}
                    >
                        <div className="relative h-full">
                            <div className="h-[60vh] flex! flex-col! items-center! justify-center! gap-2!">
                                <Pending phrase="" />
                                <div>{t("ImportData.ValidationInProgress")}</div>
                            </div>
                        </div>
                    </ModalBox>
                );
            default:
                return null;
        }
    }, [modal, dispatch, t]);

    return (
        <>
            <ModalBox
                open={importedFileStatus === "pending" || exportedFileStatus === "pending"}
                onClose={() => {}}
                showCloseButton={false}
            >
                <Pending phrase={t("History.DownloadingFile")} />
            </ModalBox>
            {modalComponenent}
            <DeleteModal
                open={modalType === "revert"}
                onClose={() => setModalType("")}
                onDelete={() => handleRollbackFile()}
                elementTitle={t("DeleteModal.TheImport")}
                description={revertModalDescription}
            />

            {selectedFile?.id && (
                <SendInvoiceModal
                    open={openModal}
                    onClose={() => setOpenModal(false)}
                    afterImportInfo={selectedFile}
                    bexioContacts={bexioContacts}
                    createdInvoice={createdInvoice}
                    failureReasonOnCreateInvoice={failureReasonOnCreateInvoice}
                    invoicePercentages={invoicePercentages}
                />
            )}

            <div className="history-wrapper">
                <header className="history-header">
                    <h1 className="history-header-heading">{t("History.Title")}</h1>
                </header>
                <main className="history-main">
                    <section className="history-main-tabs">
                        <StyledTabs value={tabsValue} onChange={handleChangeTabsValue}>
                            <Tab label={t("History.Imports")}></Tab>
                            <Tab label={t("History.Exports")}></Tab>
                        </StyledTabs>
                    </section>
                    <section className="history-main-search">
                        <SmallSearchBar
                            filter={query}
                            onSearch={handleQuery}
                            placeholder={t("History.SearchFileName")}
                            style={{ width: "100%" }}
                        />
                        <ControlFilter
                            filterLabel="Type :"
                            filterOptions={typeFilterOptions()}
                            controlValue={target}
                            controlLabel={target === "all" ? t("Filters.AllMasculine") : t(`History.Type.${target}`)}
                            onValueChange={handleTypeFilter}
                            selector
                            minWidth="172px"
                            menuMaxHeight="300px"
                        />
                        {showSourceTypeFilter && (
                            <ControlFilter
                                filterLabel="Source :"
                                filterOptions={sourceFilterOptions}
                                controlValue={sourceType}
                                controlLabel={
                                    sourceType === "all"
                                        ? t("Filters.AllMasculine")
                                        : t(`History.SourceType.${sourceType}`)
                                }
                                onValueChange={handleSourceTypeFilter}
                                selector
                                minWidth="172px"
                                menuMaxHeight="300px"
                            />
                        )}
                    </section>
                    <section className="history-main-table">
                        <LastElementTable
                            columns={headerColumns}
                            data={data}
                            max={-1}
                            stickyFooter
                            // collapseOn={"showMore"} // id of the column that will trigger the collapse
                            // collapseContent={(row) => formatCollapse(row)} // content of the collapse
                            collapseSelectedBackground="#ffff"
                            isLoading={dataLoading}
                            columnsNumber={visibleColumnsCount}
                            APIData={APIData}
                            dataCount={tabsValue === EHistoryTabs.IMPORT ? importedFilesCount : exportedFilesCount}
                        />
                    </section>
                    {!data.length &&
                        (tabsValue === EHistoryTabs.IMPORT && importedFilesStatus !== "pending" ? (
                            <EmptyState type="importBoard" sx={{ margin: "auto" }} />
                        ) : (
                            tabsValue === EHistoryTabs.EXPORT &&
                            exportedFilesStatus !== "pending" && (
                                <EmptyState type="exportBoard" sx={{ margin: "auto" }} />
                            )
                        ))}
                </main>
                <ModalBase
                    modalTitle={t("History.AnomaliesModal")}
                    open={modalType === "anomalies"}
                    onClose={() => setModalType("")}
                    widthTransition={{ width: "1200px", active: true }}
                >
                    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                        <CorrectionTable
                            errors={anomalies}
                            loadedData={formatErrorsEntries(anomalies)}
                            columns={errorColumns}
                            readonly={true}
                        />
                        <StyledButton buttonType="primary" fullWidth onClick={handleAnomaliesDownload}>
                            {t("History.DownloadAnomalies")}
                        </StyledButton>
                    </div>
                </ModalBase>
            </div>
        </>
    );
}

export default History;
